import type {
  Feature,
  MapBrowserEvent,
} from 'ol';
import type { FeatureLike } from 'ol/Feature';
import type { Geometry } from 'ol/geom';

import type { DrawToolType } from '@nora-monorepo/nora-api-client';
import { DrawLayerTypeEnum } from '@nora-monorepo/nora-api-client';
import { isNonEmptyString } from '@nora-monorepo/shared';
import { isWebViewMode } from '@nora-monorepo/ui-shared';

import type { useDrawFeatureSelectionStore } from '#nora-ui/map/draw-feature-selection/draw-feature-selection-store';
import { isDrawToolType } from '#nora-ui/map/drawtools/draw-tool-utils';
import {
  getFeatureProperties,
  isFeature,
} from '#nora-ui/map/feature-properties';
import type { NoraMap } from '#nora-ui/map/map/NoraMap';

const DESKTOP_HIT_TOLERANCE_PX = 5;
const WEBVIEW_HIT_TOLERANCE_PX = 22;

interface DrawFeatureSelectionProperties {
  drawLayerId?: string;
  drawToolType?: DrawToolType;
  hidden?: boolean;
  id?: string;
}

export enum DrawFeatureSelectionActionType {
  Deselect = 'deselect',
  OpenCandidateSelection = 'openCandidateSelection',
  Select = 'select',
}

export interface DrawFeatureSelectionAction {
  type: DrawFeatureSelectionActionType;
  feature?: Feature<Geometry>;
  features?: Feature<Geometry>[];
}

export function getSelectableDrawFeatures(
  featureLikes: FeatureLike[],
  getDrawLayerType: (drawLayerId: string) => DrawLayerTypeEnum | undefined,
): Feature<Geometry>[] {
  const featuresById = new Map<string, Feature<Geometry>>();

  for (const featureLike of featureLikes) {
    if (!isFeature(featureLike)) {
      continue;
    }

    const {
      drawLayerId,
      drawToolType,
      hidden,
      id: featureId,
    } = getFeatureProperties<DrawFeatureSelectionProperties>(featureLike);

    if (
      !isNonEmptyString(featureId)
      || !isNonEmptyString(drawLayerId)
      || !isDrawToolType(drawToolType)
      || hidden === true
      || getDrawLayerType(drawLayerId) !== DrawLayerTypeEnum.USER
    ) {
      continue;
    }

    featuresById.set(featureId, featureLike);
  }

  return [...featuresById.values()];
}

export function resolveDrawFeatureSelection(
  features: Feature<Geometry>[],
): DrawFeatureSelectionAction {
  if (features.length === 0) {
    return { type: DrawFeatureSelectionActionType.Deselect };
  }

  if (features.length === 1) {
    return {
      type: DrawFeatureSelectionActionType.Select,
      feature: features[0],
    };
  }

  return {
    type: DrawFeatureSelectionActionType.OpenCandidateSelection,
    features,
  };
}

/**
 * Checks if the feature belongs to the provided operation's id
 * @param feature
 * @param operationId
 * @param drawLayerOperationId
 */
export function isDrawFeatureInOperation(
  feature: Feature<Geometry>,
  operationId: string,
  drawLayerOperationId: string | undefined,
): boolean {
  const { drawLayerId } = getFeatureProperties<DrawFeatureSelectionProperties>(feature);

  return isNonEmptyString(drawLayerId)
    && drawLayerOperationId === operationId;
}

function isMobileInput(): boolean {
  return isWebViewMode()
    || (typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches);
}

export class DrawFeatureSelectionManager {
  private readonly onPointerMove = this.handlePointerMove.bind(this);
  private readonly onSingleClick = this.handleSingleClick.bind(this);
  private selectionStore?: ReturnType<typeof useDrawFeatureSelectionStore>;

  constructor(private readonly map: NoraMap) {}

  async setup(): Promise<void> {
    const { useDrawFeatureSelectionStore } = await import('#nora-ui/map/draw-feature-selection/draw-feature-selection-store');
    this.selectionStore = useDrawFeatureSelectionStore(this.map.getId());
    const olMap = this.map.getOlMapOrThrow();
    olMap.on('pointermove', this.onPointerMove);
    olMap.on('singleclick', this.onSingleClick);
  }

  cleanup(): void {
    const olMap = this.map.getOlMap();
    if (!olMap) {
      return;
    }

    olMap.un('pointermove', this.onPointerMove);
    olMap.un('singleclick', this.onSingleClick);
    this.selectionStore?.cleanup();
  }

  selectFeature(feature: Feature<Geometry>): void {
    this.selectionStore?.selectCandidate(feature);
  }

  /**
   * If a selected feature belongs to the operation while hiding the operation, deselect the feature
   * @param operationId
   */
  deselectFeatureForOperation(operationId: string): void {
    const selectedFeature = this.selectionStore?.selectedFeature as Feature<Geometry> | null;
    const featureId = selectedFeature?.get('drawLayerId') as string;
    const drawLayerOperationId = this.map.state.draw.getDrawLayerById(featureId)?.operationId;

    if (!selectedFeature || !isDrawFeatureInOperation(
      selectedFeature,
      operationId,
      drawLayerOperationId,
    )) {
      return;
    }

    this.selectionStore?.deselectFeature();
  }

  private handlePointerMove(event: MapBrowserEvent): void {
    if (isMobileInput()) {
      return;
    }

    this.selectionStore?.setLastPointerCoordinate(event.coordinate);
  }

  private handleSingleClick(event: MapBrowserEvent): void {
    if (!this.selectionStore || this.map.drawToolUI.activeDrawTool.value !== null) {
      return;
    }

    const action = resolveDrawFeatureSelection(this.getSelectableFeatures(event));

    switch (action.type) {
      case DrawFeatureSelectionActionType.Deselect:
        this.selectionStore.clearCandidateSelection();
        this.selectionStore.deselectFeature();
        break;
      case DrawFeatureSelectionActionType.Select:
        if (action.feature) {
          this.selectionStore.selectCandidate(action.feature);
        }
        break;
      case DrawFeatureSelectionActionType.OpenCandidateSelection:
        if (action.features) {
          this.selectionStore.deselectFeature();
          this.selectionStore.openCandidateSelection(action.features);
        }
        break;
    }
  }

  private getSelectableFeatures(event: MapBrowserEvent): Feature<Geometry>[] {
    const olMap = this.map.getOlMapOrThrow();
    const drawLayer = this.map.layers.draw.getLayer();
    const featureLikes = olMap.getFeaturesAtPixel(event.pixel, {
      hitTolerance: isMobileInput()
        ? WEBVIEW_HIT_TOLERANCE_PX
        : DESKTOP_HIT_TOLERANCE_PX,
      layerFilter: layer => layer === drawLayer,
    });

    return getSelectableDrawFeatures(
      featureLikes,
      drawLayerId => this.map.state.draw.getDrawLayerById(drawLayerId)?.type,
    );
  }
}
